import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'components';
import { VariableName } from 'components/editor/SidePanel/Variables/interfaces';
import PriceInput from 'components/priceInput';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';
import useUpdateDocumentProperties from 'components/editor/hooks/useUpdateDocumentProperties';
import type { UpdateDocumentPayloadByContentId } from 'services/documents/DocumentRepository';
import { PricingTotalType } from 'services/documents/entities';
import { updateVariableValue } from '../grid/reduxStore/allVariableSlice';
import { setSaveStatus } from '../grid/reduxStore/saveStatusSlice';
import type { RootState } from '../grid/reduxStore/Store';
import { useAppDispatch, useAppSelector } from '../grid/reduxStore/Store';
import { usePricingTotals } from '../GridDndEditor/Block/Table/PricingTotalsFooter/usePricingTotals';
import { useFieldValidator } from '../hooks/useFieldValidator';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import { selectDocumentTotal } from '../grid/reduxStore/editorSlice.ts';
import { FeatureFlags } from 'utils/featureFlags.ts';
import useFeatureFlag from 'hooks/useFeatureFlag.ts';
import {
  selectActiveCurrencySymbol,
  selectContentId,
  selectDisplayDocumentTotal,
  selectTotalType,
  setDisplayDocumentTotal,
} from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { DOLLAR_SIGN } from '../GridDndEditor/Block/Table/defaultData.tsx';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import IcoShowElement from 'components/icon/icons/IcoShowElement';
import IcoHideElement from 'components/icon/icons/IcoHideElement';

type DocumentTotal = {
  price: number;
  totalType?: PricingTotalType;
};

export function DocumentPrice() {
  const dispatch = useAppDispatch();
  const contentId = useAppSelector(selectContentId);
  const totalType = useAppSelector(selectTotalType);
  const { mutate } = useUpdateDocumentProperties();
  const { data: documentData, isLoading } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const { documentTotal: contentPrice } = usePricingTotals(totalType, documentData?.price || 0);
  const proposalTotal = useAppSelector((state: RootState) => selectDocumentTotal(state));

  const [documentTotal, setDocumentTotal] = useState<DocumentTotal>({
    price: contentPrice,
    totalType,
  });

  const { error, errorRef, displayValue, setExternalValue, handleChange, handleFocus, handleBlur } = useFieldValidator();

  const featureFlags = useFeatureFlag([FeatureFlags.currencySettings]);
  const isCurrencySettingsEnabled = featureFlags[FeatureFlags.currencySettings];
  const activeCurrencySymbol = useAppSelector(selectActiveCurrencySymbol) || DOLLAR_SIGN;
  const currencySymbol = isCurrencySettingsEnabled ? activeCurrencySymbol : DOLLAR_SIGN;

  const displayDocumentTotal = useAppSelector(selectDisplayDocumentTotal);

  const handleToggleDisplayDocumentTotal = () => {
    const newValue = !displayDocumentTotal;
    dispatch(setDisplayDocumentTotal(newValue));
    updateDocumentProperties({ displayDocumentTotal: newValue });
  };

  const previousProposalTotal = useRef<number | null>(null);
  const manualSyncedRef = useRef(false);

  const updateDocumentProperties = (payload: Omit<UpdateDocumentPayloadByContentId, 'contentId'>) => {
    mutate({ contentId, ...payload });
  };

  const updateTotalPriceValue = (price: number, type: PricingTotalType | undefined) => {
    setDocumentTotal({ price, totalType: type });
    updateDocumentProperties({ price, totalType: type });
    dispatch(
      updateVariableValue({
        variableName: VariableName.Total,
        newValue: price,
      })
    );
  };

  const revertToCalculated = () => {
    const calculatedTotal = proposalTotal ?? 0;
    setDocumentTotal({ price: calculatedTotal, totalType: PricingTotalType.CALCULATED });
    updateTotalPriceValue(calculatedTotal, PricingTotalType.CALCULATED);
    setExternalValue(calculatedTotal);
  };

  const handleOnChangeTotal = (raw: string, num: number | null) => {
    if (raw.replace(/,/g, '') === '') {
      setDocumentTotal({ totalType: PricingTotalType.CALCULATED, price: proposalTotal || 0 });
      setExternalValue(proposalTotal ?? 0);
      return;
    }

    if (num === null) return;

    if (num === 0) {
      setDocumentTotal({ totalType: PricingTotalType.CALCULATED, price: proposalTotal || 0 });
      setExternalValue(proposalTotal ?? 0);
      return;
    }

    setDocumentTotal({ totalType: PricingTotalType.MANUAL, price: num });
  };

  const handleOnBlurTotal = (num: number) => {
    if (isNaN(num)) {
      if (!errorRef.current) revertToCalculated();
      return;
    }
    updateTotalPriceValue(num, documentTotal.totalType);
  };

  useEffect(() => {
    if (proposalTotal == null || isLoading) return;
    if (previousProposalTotal.current === proposalTotal) return;

    previousProposalTotal.current = proposalTotal;

    if (documentTotal.totalType === PricingTotalType.MANUAL) return;

    setExternalValue(proposalTotal);

    if (!errorRef.current) {
      updateTotalPriceValue(proposalTotal, PricingTotalType.CALCULATED);
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVED }));
    } else {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
    }
  }, [proposalTotal, totalType, isLoading]);

  useEffect(() => {
    if (totalType !== PricingTotalType.MANUAL) {
      manualSyncedRef.current = false;
      return;
    }
    if (manualSyncedRef.current) return;
    if (!documentData?.price) return;

    manualSyncedRef.current = true;
    const manualPrice = Number(documentData.price);
    setDocumentTotal({ price: manualPrice, totalType: PricingTotalType.MANUAL });
    setExternalValue(manualPrice);
  }, [documentData?.price, totalType]);

  const visibilityTooltip = displayDocumentTotal ? 'Total is visible in the preview' : 'Total is hidden from the preview';
  const visibilityAriaLabel = displayDocumentTotal ? 'Hide total from preview' : 'Show total in preview';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Tooltip placement="bottom" title={visibilityTooltip}>
        <IconButton
          size="small"
          onClick={handleToggleDisplayDocumentTotal}
          aria-label={visibilityAriaLabel}
          sx={{ mr: 0.5, color: 'inherit' }}
        >
          {displayDocumentTotal ? <IcoShowElement /> : <IcoHideElement />}
        </IconButton>
      </Tooltip>
      <span>{currencySymbol}&nbsp;</span>
      <Tooltip className="tooltip__error" placement="bottom" title={error ?? ''}>
        <>
          <PriceInput
            value={displayValue}
            error={error}
            onChange={(raw) => {
              const num = handleChange(raw);
              handleOnChangeTotal(raw, num);
              return num;
            }}
            onBlur={() => {
              const num = handleBlur();
              handleOnBlurTotal(num);
            }}
            onFocus={handleFocus}
          />
        </>
      </Tooltip>
    </Box>
  );
}
